/**
 * №29 (D-L1, S0 → S2) — the CENTRE zone's head: what the stage is currently holding.
 *
 * The probe's `.crumb`, which every mockup has:
 *
 *   Vantage  /  Disclosure review   ● running · 18m          [Pause] [⤓ Export run]
 *
 * At S0 this named the subject and its anchor and ONLY those, because the rest
 * belongs to whichever subject is on stage and the subjects hadn't landed yet. S2
 * lands them, so the head now carries all four parts:
 *
 *   - the ANCHOR, from the shell (`useAppScope` → the matrix row). Never restated by
 *     a surface: one resolution, three zones, which is the whole of D-SS2.
 *   - the subject's own WORKING TITLE and LIVE STATE, declared by the routed page
 *     through `useStageHead` (see `stage-head.tsx` for why it is declared rather
 *     than derived).
 *   - the subject's VERBS, portalled in through `<StageActions>` — the probe's
 *     `Run all` / `Share` / `Re-derive` / `Pause` band. This is the per-subject
 *     toolbar grammar S1 named and left here.
 *
 * The subject KIND chip is gone from the default case, and that is a fix rather than
 * a tidy-up: "Record" beside a record's own title said nothing a reader did not
 * already know, and it crowded out the thing that IS worth saying. It survives only
 * where a subject declares no title of its own, so the head is never empty.
 *
 * ## Q3 — the stage head owns the anchor (David's S1 STOP question, executed)
 *
 * On a chat subject there used to be two things saying "Acme — engagement": this
 * head, and the conversation's own `@ anchor · with {hire}` chip. Drawn both ways at
 * S1 (`docs/29-shell-options/Q3-anchor-on-stage.png`); B was the recommendation, to
 * be executed by S2 because S2 was rebuilding this head anyway. It is executed: on a
 * chat subject the conversation drops the anchor half of its chip and keeps
 * "· with {hire}", so the ACTOR is still named before you speak (D-AF4 intact) and
 * the anchor is named once. Everywhere else the conversation is in the rail, far from
 * this head, and keeps its full chip.
 *
 * ## №53.3.1 S0 slice 3 (D-CR26) — RECORD subjects own their own identity now
 *
 * A record page carries its own `.rechead` — serif title, status pill, lifecycle
 * verbs (`MatterCockpitPage.tsx`'s own header block). This band's fallback used to
 * render "MAT-0004 · record" directly above it (the anchor chip + the `record` kind
 * label, since a record page stopped declaring a `useStageHead` title in slice 2) —
 * the exact same "a kind chip beside a title says nothing a reader did not already
 * know" duplication this file's own header names for the default case above, just
 * one level down: not title-vs-kind, but kind-vs-the-page's-OWN-title.
 *
 * Suppressed here, scoped to `subject === "record"` alone — table/run/desk/chat
 * subjects declare no header of their own and keep the full band. The nav-drawer
 * TOGGLE survives regardless: at a narrow viewport it is the record page's ONLY way
 * to open the rail nav (`AppShellLayout.tsx`'s `setNavOpen`), and dropping it would
 * be exactly the kind of un-ruled usability call `BackBar`'s own preservation next
 * to this file was — so a reduced band still mounts, and `app-chrome.css` collapses
 * it to nothing (not merely unstyled) above the narrow breakpoint instead of this
 * component unmounting the one control still needed below it.
 */
import { useNavigate } from "react-router";
import { useAppScope } from "../../hooks/use-app-scope";
import { useStageActionSlot, useStageHeadValue } from "./stage-head";
import type { SubjectKind } from "./route-anchor-matrix";

/** What the stage is holding, in the user's words — never an engine identifier.
 *  Rendered only as the fallback when a subject declares no working title. */
const SUBJECT_LABEL: Record<SubjectKind, string> = {
  cards: "Needs you",
  table: "Table",
  record: "Record",
  run: "Run",
  desk: "Desk",
  chat: "Chat",
  intake: "Intake",
  form: "Settings",
};

export function SubjectHeader({
  subject,
  onOpenNav,
  subjectRoute,
}: {
  subject: SubjectKind;
  /** Narrow only (§5): opens the nav drawer. Hidden by CSS above the breakpoint. */
  onOpenNav: () => void;
  /** №29 (S1) — on a CHAT subject: where the conversation came from, so it can be
   *  handed back to the rail. Null on every other subject. */
  subjectRoute?: string | null;
}) {
  const { chip } = useAppScope();
  const { title, state } = useStageHeadValue();
  const actionSlot = useStageActionSlot();
  const navigate = useNavigate();

  // №53.3.1 S0 slice 3 (D-CR26) — see this file's own header. The record's `.rechead`
  // owns identity now; this band reduces to the one thing it still must carry
  // regardless of subject — the narrow-viewport nav-drawer toggle (`app-chrome.css`
  // hides the rest of `.stagehead--record` above that breakpoint, not this branch,
  // so the toggle is never unmounted).
  if (subject === "record") {
    return (
      <div className="stagehead stagehead--record" data-testid="stagehead" data-subject={subject}>
        <button
          type="button"
          className="stagehead__nav"
          onClick={onOpenNav}
          aria-label="Open navigation"
          data-testid="stagehead-nav"
        >
          <i className="ti ti-menu-2" aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <div className="stagehead" data-testid="stagehead" data-subject={subject}>
      <button
        type="button"
        className="stagehead__nav"
        onClick={onOpenNav}
        aria-label="Open navigation"
        data-testid="stagehead-nav"
      >
        <i className="ti ti-menu-2" aria-hidden="true" />
      </button>
      <b className="stagehead__name" data-testid="stagehead-anchor">{chip}</b>
      {title ? (
        <span className="stagehead__title" data-testid="stagehead-title">
          <span className="stagehead__sep" aria-hidden="true">/</span>
          {title}
        </span>
      ) : (
        <span className="stagehead__kind" data-testid="stagehead-subject">{SUBJECT_LABEL[subject]}</span>
      )}
      {/* The live state, when the subject genuinely has one. No chip is a state too
          — an idle surface says nothing rather than "● idle" (D-ST5). */}
      {state && (
        <span className={`stagestate stagestate--${state.tone}`} data-testid="stagehead-state" data-tone={state.tone}>
          {state.tone === "running" && <span className="stagestate__dot kuvio-status-active-pulse" aria-hidden="true" />}
          {state.label}
        </span>
      )}
      <span className="spacer" />
      {/* The subject's own verbs land here, portalled from the routed page. */}
      <span className="stagehead__actions" ref={actionSlot} data-testid="stagehead-actions" />
      {/* №29 (S1) — the other half of "which one is the subject is a user choice".
          The rail's Chat tab promotes the conversation onto the stage; this hands it
          back. One conversation either way — the move re-parents it, never re-creates
          it (see conversation-host.tsx). */}
      {subject === "chat" && subjectRoute && (
        <button
          type="button"
          className="stagehead__demote"
          onClick={() => navigate(subjectRoute)}
          data-testid="stagehead-demote"
        >
          <i className="ti ti-arrow-bar-to-right" aria-hidden="true" /> Move to the rail
        </button>
      )}
    </div>
  );
}
